import React, { useState } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { submitContactForm, testContactAPI } from "../api";
import "./Contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [errors, setErrors] = useState({});
  const [debugMode, setDebugMode] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (formData.phone && formData.phone.trim()) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        newErrors.phone = "Please enter a valid 10-digit phone number";
      }
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message should be at least 10 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (phone) => {
    const digits = phone.replace(/\D/g, '');
    
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 5) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formattedPhone });
    
    if (errors.phone) {
      setErrors({ ...errors, phone: "" });
    }
  };

  const handleTestAPI = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    
    try {
      const result = await testContactAPI();
      console.log('Test API Result:', result);
      
      setMessage({
        type: "info",
        text: `API Test: ${JSON.stringify(result, null, 2)}`
      });
    } catch (error) {
      console.error('Test API Error:', error);
      setMessage({
        type: "error",
        text: `Test failed: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setMessage({ type: "", text: "" });
    
    if (!validateForm()) {
      setMessage({
        type: "error",
        text: "Please fix the errors in the form"
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setLoading(true);
    
    try {
      const apiData = {
        ...formData,
        phone: formData.phone ? formData.phone.replace(/\D/g, '') : ''
      };
      
      console.log('Submitting contact form:', apiData);
      
      const response = await submitContactForm(apiData);
      console.log('API Response:', response);
      
      if (response.success) {
        setMessage({
          type: "success",
          text: response.message || "Thank you! We will get back to you soon."
        });
        
        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
        });
        
        setErrors({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        setTimeout(() => {
          setMessage({ type: "", text: "" });
        }, 5000);
      } else {
        setMessage({
          type: "error",
          text: response.message || "Failed to submit form. Please try again."
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      
      // Check if it's a 404 error
      if (error.response?.status === 404) {
        setMessage({
          type: "error",
          text: "The contact form service is temporarily unavailable. Please email us directly at support@volumaire.com or try again later."
        });
      } else {
        setMessage({
          type: "error",
          text: "An error occurred. Please try again later or contact support."
        });
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const toggleDebug = () => {
    setDebugMode(!debugMode);
  };

  return (
    <>
      <Header />

      <section className="contact-section">
        {/* Debug Toggle - Remove in production */}
        <div className="debug-toggle" onClick={toggleDebug}>
          🛠️
        </div>

        <h1 className="contact-title">
          Need help? Get in <br /> touch.
        </h1>

        <p className="contact-subtitle">
          Contact one of our experts and we'll get back to you as soon as possible.
        </p>

        {/* Business Information */}
        <div className="contact-business-info">
          <h3>Volumaire Health And Beauty Pvt Ltd.</h3>
          <p>
            701, Maitri Marigold,<br />
            Maitri Gardens, Pokhran Road No. 2,<br />
            Thane West – 400601
          </p>
          <div className="contact-phone">
            <span className="phone-label">Phone:</span>
            <a href="tel:+919819583700" className="phone-number">+91 98195 83700</a>
          </div>
          <div className="contact-email">
            <span className="email-label">Email:</span>
            <a href="mailto:support@volumaire.com" className="email-address">support@volumaire.com</a>
          </div>
        </div>

        {/* Success/Error Messages */}
        {message.text && (
          <div className={`contact-message ${message.type}`}>
            <div className="message-content">
              {message.type === "success" ? "✓ " : message.type === "info" ? "ℹ " : "⚠ "}
              {message.text}
            </div>
            <button 
              className="message-close" 
              onClick={() => setMessage({ type: "", text: "" })}
            >
              ×
            </button>
          </div>
        )}

        {/* Debug Panel */}
        {debugMode && (
          <div className="debug-panel">
            <h4>Debug Mode</h4>
            <p>Form Data: {JSON.stringify(formData)}</p>
            <p>Errors: {JSON.stringify(errors)}</p>
            <button 
              onClick={handleTestAPI}
              disabled={loading}
              className="debug-btn"
            >
              Test API Connection
            </button>
          </div>
        )}

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-field">
              <input
                type="text"
                name="name"
                placeholder="Name *"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "error" : ""}
                required
                disabled={loading}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-field">
              <input
                type="tel"
                name="phone"
                placeholder="Phone number"
                value={formData.phone}
                onChange={handlePhoneChange}
                className={errors.phone ? "error" : ""}
                maxLength="12"
                disabled={loading}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-field">
            <input
              type="email"
              name="email"
              placeholder="Email *"
              required
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? "error" : ""}
              disabled={loading}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-field">
            <textarea
              name="message"
              placeholder="Your message or query *"
              value={formData.message}
              onChange={handleChange}
              rows="5"
              className={errors.message ? "error" : ""}
              disabled={loading}
              required
            />
            {errors.message && <span className="error-message">{errors.message}</span>}
          </div>

          <div className="form-footer">
            <button 
              type="submit" 
              className="send-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  SENDING...
                </>
              ) : (
                "SEND"
              )}
            </button>
          </div>
        </form>
      </section>

      {/* <Footer /> */}
    </>
  );
};

export default Contact;